import React, { useMemo, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, message, Tag, Empty, Alert, Space,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  useListCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
} from '../services/adminApi';
import AdminPageHeader, { adminPanelStyle } from './AdminPageHeader';

function indentName(name, depth) {
  if (!depth) return name;
  return `${'— '.repeat(depth)}${name}`;
}

export default function CategoriesPage() {
  const { data, isLoading, isError, error } = useListCategoriesQuery();
  const [createCategory] = useCreateCategoryMutation();
  const [updateCategory] = useUpdateCategoryMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const parentOptions = useMemo(() => (
    (data?.items || []).map((c) => ({
      value: c.id,
      label: `${indentName(c.name, c.depth)} (${c.slug})`,
    }))
  ), [data]);

  const handleOpenCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleOpenEdit = (category) => {
    setEditing(category);
    form.setFieldsValue({
      name: category.name,
      slug: category.slug,
      icon: category.icon,
    });
    setModalOpen(true);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      if (editing) {
        await updateCategory({ categoryId: editing.id, ...values }).unwrap();
        message.success('Category updated');
      } else {
        await createCategory({
          ...values,
          parentId: values.parentId || null,
        }).unwrap();
        message.success('Category created');
      }
      setModalOpen(false);
    } catch (err) {
      message.error(err.data?.title || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, row) => (
        <span style={{ fontFamily: 'var(--df-font-display)', fontWeight: row.depth === 0 ? 700 : 500 }}>
          {indentName(name, row.depth)}
        </span>
      ),
    },
    { title: 'Slug', dataIndex: 'slug', key: 'slug', ellipsis: true },
    {
      title: 'Depth',
      dataIndex: 'depth',
      key: 'depth',
      width: 80,
      render: (d) => <Tag>{d}</Tag>,
    },
    {
      title: 'Nested set',
      key: 'nested',
      width: 120,
      render: (_, row) => (
        <span style={{ fontSize: 12, color: 'var(--df-text-secondary)' }}>
          [{row.lft}, {row.rgt}]
        </span>
      ),
    },
    {
      title: 'Icon',
      dataIndex: 'icon',
      key: 'icon',
      width: 140,
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button size="small" onClick={() => handleOpenEdit(record)}>Edit</Button>
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Categories"
        subtitle="Nested-set catalog tree — create children under a parent; edit name, slug, and icon"
        extra={(
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
            Add category
          </Button>
        )}
      />

      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error?.data?.title || 'Failed to load categories'}
        />
      )}

      <div style={adminPanelStyle}>
        <Table
          dataSource={data?.items}
          columns={columns}
          loading={isLoading}
          rowKey="id"
          pagination={{ pageSize: 25 }}
          size="middle"
          locale={{ emptyText: <Empty description="No categories" /> }}
        />
      </div>

      <Modal
        title={editing ? 'Edit category' : 'Create category'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, min: 2, message: 'Name is required' }]}
          >
            <Input placeholder="e.g. Gaming Laptops" />
          </Form.Item>
          <Form.Item
            name="slug"
            label="Slug"
            extra={editing ? undefined : 'Optional — auto-generated from name if blank'}
            rules={[
              {
                pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                message: 'Lowercase kebab-case only',
              },
            ]}
          >
            <Input placeholder="gaming-laptops" />
          </Form.Item>
          <Form.Item name="icon" label="Icon key">
            <Input placeholder="LaptopOutlined" />
          </Form.Item>
          {!editing && (
            <Form.Item name="parentId" label="Parent">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Root category (no parent)"
                options={parentOptions}
              />
            </Form.Item>
          )}
          {editing && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Parent moves are not supported in this editor (nested-set rebuild). Create a new child under the desired parent instead."
            />
          )}
        </Form>
        <Space />
      </Modal>
    </div>
  );
}
